/**
 * Lead/flagship model slug per brand. Drives which vehicle image renders
 * as the transparent ghost overlay on /brands/<slug> and
 * /<service>/<brand>/<city> programmatic pages.
 *
 * If a brand isn't listed, the first model with a valid image in
 * brands_data.json is used as a graceful fallback.
 *
 * The slugs must exactly match the `slug` field on entries in
 * brands_data.json (NOT the human-readable model label).
 */

import brandsData from '../data/brands_data.json';

export const LEAD_MODEL_BY_BRAND = {
  // ── dual-vehicle-type brands: type-qualified keys take priority so a CAR
  //    page never shows the brand's two-wheeler (and vice-versa).
  'honda:car': 'city',
  'mahindra:car': 'scorpio',
  'bmw:bike': 'g-310',
  'honda:bike': 'activa',
  'mahindra:bike': 'stallio',
  'bmw:car': 'x1',

  // ── bikes
  bajaj: 'pulsar',
  hero: 'splendor',
  honda: 'activa',
  tvs: 'apache',
  'royal-enfield': 'classic-350',
  yamaha: 'fz',
  suzuki: 'access',
  ktm: 'duke',
  kawasaki: 'ninja',
  'harley-davidson': 'street-750',
  triumph: 'street-triple',
  ducati: 'panigale-v4',
  aprilia: 'rs',
  benelli: 'tnt-300',
  jawa: 'jawa-42',
  yezdi: 'adventure',
  vespa: 'vxl',
  mahindra: 'mojo',
  husqvarna: 'svartpilen',
  indian: 'scout',
  hyosung: 'gt650',
  um: 'commando',
  'fb-mondial': 'hps-300',

  // ── cars
  'maruti-suzuki': 'swift',
  hyundai: 'creta',
  tata: 'nexon',
  toyota: 'fortuner',
  kia: 'seltos',
  mg: 'hector',
  volkswagen: 'virtus',
  skoda: 'kushaq',
  renault: 'kwid',
  nissan: 'magnite',
  jeep: 'compass',
  ford: 'endeavour',
  mercedes: 'c-class',
  audi: 'a4',
  bmw: 'x1',
  mini: 'cooper',
  jaguar: 'xe',
  'land-rover': 'discovery',
  porsche: 'macan',
  volvo: 'xc60',
  lexus: 'es',
  bentley: 'continental-gt',
  ferrari: '488',
  maserati: 'ghibli',
  lamborghini: 'urus',
  'rolls-royce': 'ghost',
};

/**
 * Resolve a brand slug → the best model entry to use for the hero image.
 * Returns the model object (with .imagePath) or null if the brand has no
 * models with images.
 */
export function getLeadModelForBrand(brandSlug, vehicleType) {
  if (!brandSlug) return null;
  // Search the brand's OWN vehicle-type list first so dual-type brands
  // (honda, bmw, mahindra) resolve to the correct vehicle for the page.
  // vehicleType is optional — when omitted we keep the legacy bike-first order.
  const carList = brandsData.car || [];
  const bikeList = brandsData.bike || [];
  const primary = vehicleType === 'car' ? carList : bikeList;
  const secondary = vehicleType === 'car' ? bikeList : carList;
  const brand = primary.find((b) => b.slug === brandSlug)
    || secondary.find((b) => b.slug === brandSlug);
  if (!brand || !brand.models || !brand.models.length) return null;

  // Prefer a type-qualified hint (e.g. 'honda:car'), then the plain slug hint.
  const hint = (vehicleType && LEAD_MODEL_BY_BRAND[`${brandSlug}:${vehicleType}`])
    || LEAD_MODEL_BY_BRAND[brandSlug];
  if (hint) {
    const preferred = brand.models.find((m) => m.imagePath && m.slug === hint);
    if (preferred) return preferred;
  }
  return brand.models.find((m) => m.imagePath) || null;
}

/**
 * Webpack require helper — returns a resolvable image URL or null.
 * Kept here so callers don't repeat the try/catch boilerplate.
 */
export function tryRequireVehicleImage(imagePath) {
  if (!imagePath) return null;
  try {
    const img = require(`../images/vehicle_images/${imagePath}`);
    return typeof img === 'object' ? img.default : img;
  } catch (e) {
    return null;
  }
}

/** One-shot: brand slug (+ optional vehicleType) → resolved image URL (or null). */
export function resolveBrandVehicleImage(brandSlug, vehicleType) {
  const lead = getLeadModelForBrand(brandSlug, vehicleType);
  return lead ? tryRequireVehicleImage(lead.imagePath) : null;
}
